import React, { useRef, useMemo, useState, useEffect } from "react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import { Input } from '@/components/ui/input';

const libraries = ["places"];

/**
 * Local error boundary that contains any crash inside the Google Places
 * `<Autocomplete>` wrapper to this one component. Without this, errors
 * thrown imperatively by the minified Maps SDK (e.g. "can't access property
 * 'eK', p is undefined") unmount the entire Profile route because they
 * happen outside React's render cycle and bubble up to React's reconciler.
 *
 * On crash we fall back to a plain `<Input>` so the user can still type/save
 * their address manually — they just lose the autocomplete suggestions.
 */
class PlacesErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.warn('[AddressAutocompleteV2] Places crashed — falling back to plain input.', error, info?.componentStack);
  }
  render() {
    if (this.state.crashed) return this.props.fallback;
    return this.props.children;
  }
}

/**
 * Returns true only when window.google.maps.places is *actually* available.
 *
 * `useLoadScript` from @react-google-maps/api can report `isLoaded=true`
 * before all sub-modules of the Maps SDK are wired up, which is what
 * triggers the "p is undefined" crash inside Autocomplete.getPlace().
 * This polls briefly after `isLoaded` flips true to confirm the places
 * namespace exists before we attempt to mount the Autocomplete wrapper.
 */
function usePlacesReady(isLoaded) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!isLoaded) {
      setReady(false);
      return undefined;
    }
    if (window.google?.maps?.places?.Autocomplete) {
      setReady(true);
      return undefined;
    }
    // Poll briefly (up to ~2s) in case the places namespace lags isLoaded.
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (window.google?.maps?.places?.Autocomplete) {
        setReady(true);
        clearInterval(id);
      } else if (attempts >= 20) {
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, [isLoaded]);
  return ready;
}

/**
 * AddressAutocompleteV2 - Specialized for Profile page Home Address section
 * Uses shadcn Input component and extracts full address details.
 *
 * Wrapped in React.memo so parent-level re-renders (e.g. the live preview
 * string under the new First/Last name inputs that recomputes on every
 * keystroke) don't churn this component and re-trigger the fragile
 * isLoaded→Autocomplete swap inside react-google-maps internals.
 */
function AddressAutocompleteV2({ value, onChange, onSelect, placeholder = "Start typing your address..." }) {
  const acRef = useRef(null);

  // Load the Places library
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });
  const placesReady = usePlacesReady(isLoaded);

  const options = useMemo(
    () => ({
      componentRestrictions: { country: "us" },
      fields: [
        "place_id",
        "formatted_address",
        "address_components",
        "geometry",
      ],
    }),
    []
  );

  const handlePlaceChanged = () => {
    // Guard 1+2+3: window.google.maps.places must exist.
    // Guard 4: the Autocomplete instance ref must be populated.
    if (!window.google?.maps?.places || !acRef.current) {
      console.warn('[AddressAutocompleteV2] handlePlaceChanged skipped — Places not ready.');
      return;
    }
    let place;
    try {
      place = acRef.current.getPlace?.();
    } catch (err) {
      console.warn('[AddressAutocompleteV2] getPlace() threw — ignoring.', err);
      return;
    }
    if (!place || !place.place_id) return;

    const comps = place.address_components || [];
    const get = (t) =>
      comps.find((c) => c.types.includes(t))?.long_name || "";
    const getShort = (t) =>
      comps.find((c) => c.types.includes(t))?.short_name || "";

    // Extract address components
    const streetNumber = get("street_number");
    const route = get("route");
    const city =
      get("locality") ||
      get("sublocality") ||
      get("administrative_area_level_3") ||
      get("administrative_area_level_2") ||
      "";
    const state = getShort("administrative_area_level_1"); // e.g., "NY"
    const zipCode = get("postal_code") || "";
    const country = getShort("country") || "US";

    // Construct address line 1 (street address)
    const addressLine1 = [streetNumber, route].filter(Boolean).join(" ") || place.formatted_address;

    try {
      onSelect({
        formatted_address: place.formatted_address || "",
        address_line1: addressLine1,
        city,
        state,
        zip_code: zipCode,
        country,
        place_id: place.place_id,
        lat: place.geometry?.location?.lat?.(),
        lng: place.geometry?.location?.lng?.(),
      }, place.formatted_address);
    } catch (err) {
      console.warn('[AddressAutocompleteV2] onSelect handler threw — ignoring.', err);
    }
  };

  const inputElement = (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full"
    />
  );

  // Until Places is *actually* ready, render the plain input so users can
  // type/save their address manually. After it's ready, swap in the
  // Autocomplete wrapper. Wrapped in PlacesErrorBoundary so a crash inside
  // Google's SDK doesn't take down the whole Profile route.
  if (!placesReady) return inputElement;
  return (
    <PlacesErrorBoundary fallback={inputElement}>
      <Autocomplete
        onLoad={(ac) => (acRef.current = ac)}
        onPlaceChanged={handlePlaceChanged}
        options={options}
      >
        {inputElement}
      </Autocomplete>
    </PlacesErrorBoundary>
  );
}

export default React.memo(AddressAutocompleteV2);
